import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '@store/index'
import type {
  ContentFlag,
  ModerationLog,
  ModerationRule,
  ModerationMetrics,
  PaginatedResult,
  ReportContentRequest,
  PerformActionRequest,
  SuspendUserRequest,
  CreateRuleRequest,
  UserSuspension,
} from '@/shared/types/moderation'

// User-facing moderation API (reporting content)
export const moderationApi = createApi({
  reducerPath: 'moderationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/moderation',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth?.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['ContentFlags'],
  endpoints: (builder) => ({
    // User-facing: report content
    reportContent: builder.mutation<{ success: boolean; data: ContentFlag }, ReportContentRequest>({
      query: (body) => ({ url: '/flag', method: 'POST', body }),
      invalidatesTags: ['ContentFlags'],
    }),
  }),
})

// Admin moderation API (queue, actions, rules)
export const adminModerationApi = createApi({
  reducerPath: 'adminModerationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/moderation',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth?.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Queue', 'Rules', 'Metrics', 'AuditLogs'],
  endpoints: (builder) => ({
    // Admin: get moderation queue
    getModerationQueue: builder.query<
      { success: boolean; data: PaginatedResult<ContentFlag> },
      { status?: string; contentType?: string; page?: number; limit?: number }
    >({
      query: (params) => ({ url: '/queue', params }),
      providesTags: ['Queue'],
    }),

    // Admin: get single flagged item
    getModerationItem: builder.query<{ success: boolean; data: ContentFlag }, string>({
      query: (flagId) => `/queue/${flagId}`,
      providesTags: (_result, _error, id) => [{ type: 'Queue', id }],
    }),

    // Admin: perform moderation action
    performAction: builder.mutation<{ success: boolean; data: ModerationLog }, PerformActionRequest>({
      query: ({ flagId, ...body }) => ({ url: `/queue/${flagId}/action`, method: 'POST', body }),
      invalidatesTags: ['Queue', 'Metrics', 'AuditLogs'],
    }),

    // Admin: suspend user
    suspendUser: builder.mutation<{ success: boolean; data: UserSuspension }, SuspendUserRequest>({
      query: (body) => ({ url: '/suspend', method: 'POST', body }),
      invalidatesTags: ['Queue', 'Metrics'],
    }),

    // Admin: lift suspension
    liftSuspension: builder.mutation<{ success: boolean; data: UserSuspension }, string>({
      query: (suspensionId) => ({ url: `/suspend/${suspensionId}`, method: 'DELETE' }),
    }),

    // Admin: get metrics
    getModerationMetrics: builder.query<{ success: boolean; data: ModerationMetrics }, void>({
      query: () => '/stats',
      providesTags: ['Metrics'],
    }),

    // Admin: get audit logs
    getAuditLogs: builder.query<
      { success: boolean; data: PaginatedResult<ModerationLog> },
      { page?: number; limit?: number }
    >({
      query: (params) => ({ url: '/audit-logs', params }),
      providesTags: ['AuditLogs'],
    }),

    // Admin: get rules
    getModerationRules: builder.query<{ success: boolean; data: ModerationRule[] }, void>({
      query: () => '/rules',
      providesTags: ['Rules'],
    }),

    // Admin: create rule
    createRule: builder.mutation<{ success: boolean; data: ModerationRule }, CreateRuleRequest>({
      query: (body) => ({ url: '/rules', method: 'POST', body }),
      invalidatesTags: ['Rules'],
    }),

    // Admin: update rule
    updateRule: builder.mutation<
      { success: boolean; data: ModerationRule },
      { id: string; data: Partial<ModerationRule> }
    >({
      query: ({ id, data }) => ({ url: `/rules/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Rules'],
    }),

    // Admin: delete rule
    deleteRule: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/rules/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Rules'],
    }),
  }),
})

export const {
  useReportContentMutation,
} = moderationApi

export const {
  useGetModerationQueueQuery,
  useGetModerationItemQuery,
  usePerformActionMutation,
  useSuspendUserMutation,
  useLiftSuspensionMutation,
  useGetModerationMetricsQuery,
  useGetAuditLogsQuery,
  useGetModerationRulesQuery,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useDeleteRuleMutation,
} = adminModerationApi
